import { createId, db, nowIso } from '@/modules/db'
import { reportCareEventToGreenGoods } from '@/modules/green-goods'
import type { Job } from '@/modules/job-queue/db'
import type { JobHandler } from '@/modules/job-queue/queue'
import { registerHandler } from '@/modules/job-queue/queue'
import type { Stage } from '@/types/common'
import type { GameType } from '@/types/game'

// Lazy-load EAS SDK modules to avoid crashing the app on startup.
// The EAS SDK performs Object.defineProperty during module init which
// can fail in certain browser/bundler environments.
async function loadAttestation() {
  const [{ createEASOffchainAttestation }, { toBytes32Hash }] = await Promise.all([
    import('@/modules/attestation/eas-offchain'),
    import('@/modules/attestation/schemas'),
  ])
  return { createEASOffchainAttestation, toBytes32Hash }
}

async function loadAtproto() {
  const { ensureIdentityLinkAttestation, publishAtprotoProofRef } = await import(
    '@/modules/atproto/records'
  )
  return { ensureIdentityLinkAttestation, publishAtprotoProofRef }
}

function toUnixSeconds(timestamp: string): number {
  const parsed = new Date(timestamp).getTime()
  if (!Number.isFinite(parsed)) return Math.floor(Date.now() / 1000)
  return Math.floor(parsed / 1000)
}

function gameTypeToEnum(gameType: GameType): number {
  if (gameType === 'tic-tac-toe') return 0
  if (gameType === 'mancala') return 1
  return 2
}

function modeToEnum(mode: 'device' | 'local' | 'world' | undefined): number {
  if (mode === 'device') return 0
  if (mode === 'local') return 1
  if (mode === 'world') return 2
  return 0
}

function stageToEnum(stage: Stage): number {
  if (stage === 'Seed') return 0
  if (stage === 'Sprout') return 1
  if (stage === 'Bloom') return 2
  return 3
}

async function publishProofRefIfLinked(
  playerId: string,
  schemaUid: string,
  attestationUid: string,
  kind: 'careEvent' | 'gameResult' | 'evolution' | 'identityLink',
): Promise<void> {
  const linked = await db.atprotoAccounts.where('playerId').equals(playerId).first()
  if (!linked) return
  const existing = await db.proofRefs.where('attestationUid').equals(attestationUid).first()
  if (existing) return
  const { publishAtprotoProofRef } = await loadAtproto()
  await publishAtprotoProofRef(playerId, linked.did, {
    easChainId: 84532,
    schemaUid,
    attestationUid,
    kind,
  })
}

const careEventHandler: JobHandler = {
  type: 'care-event',
  async process(job: Job) {
    const careEventId = String(job.payload.careEventId ?? '')
    const playerId = String(job.payload.playerId ?? '')
    if (!careEventId || !playerId) {
      throw new Error('care-event job missing careEventId or playerId')
    }

    const [careEvent, plant, player, linkedIdentity] = await Promise.all([
      db.careEvents.get(careEventId),
      db.plants.get(String(job.payload.plantId ?? '')),
      db.players.get(playerId),
      db.atprotoAccounts.where('playerId').equals(playerId).first(),
    ])
    if (!careEvent || !plant || !player) {
      throw new Error('care-event references missing care event, plant, or player')
    }

    const { createEASOffchainAttestation, toBytes32Hash } = await loadAttestation()
    const attestation = await createEASOffchainAttestation({
      schemaKey: 'wefa.careEvent',
      recipient: player.address,
      data: {
        playerId,
        playerDid: linkedIdentity?.did ?? '',
        plantId: plant.id,
        actionMask: toBytes32Hash(careEvent.actions),
        photoHash: toBytes32Hash(careEvent.photoUrl ?? ''),
        streakDays: plant.careStreak,
        energyEarned: careEvent.energyEarned,
        timestamp: toUnixSeconds(careEvent.timestamp),
      },
    })

    await db.transaction('rw', db.attestations, db.careEvents, db.eventLog, async () => {
      await db.attestations.put(attestation)
      await db.careEvents.update(careEvent.id, { synced: true })
      await db.eventLog.add({
        id: createId(),
        kind: 'attestation-created',
        payload: {
          jobId: job.id,
          kind: 'careEvent',
          attestationUid: attestation.id,
        },
        timestamp: nowIso(),
      })
    })

    await reportCareEventToGreenGoods({
      careEventId: careEvent.id,
      playerId,
      plantId: plant.id,
      energyEarned: careEvent.energyEarned,
    })

    await publishProofRefIfLinked(playerId, attestation.schemaId, attestation.id, 'careEvent')
  },
}

const nurtureEventHandler: JobHandler = {
  type: 'nurture-event',
  async process(job: Job) {
    const nurtureEventId = String(job.payload.nurtureEventId ?? '')
    const playerId = String(job.payload.playerId ?? '')
    if (!nurtureEventId || !playerId) {
      throw new Error('nurture-event job missing nurtureEventId or playerId')
    }

    const [nurtureEvent, player] = await Promise.all([
      db.nurtureEvents.get(nurtureEventId),
      db.players.get(playerId),
    ])
    if (!nurtureEvent || !player) {
      throw new Error('nurture-event references missing nurture event or player')
    }

    const { createEASOffchainAttestation } = await loadAttestation()
    const attestation = await createEASOffchainAttestation({
      schemaKey: 'wefa.nurtureEvent',
      recipient: player.address,
      data: {
        nurtureEventId: nurtureEvent.id,
        creatureId: nurtureEvent.creatureId,
        playerId: nurtureEvent.playerId,
        energyCost: nurtureEvent.energyCost,
        xpGained: nurtureEvent.xpGained,
        timestamp: toUnixSeconds(nurtureEvent.timestamp),
      },
    })

    await db.transaction('rw', db.attestations, db.nurtureEvents, db.eventLog, async () => {
      await db.attestations.put(attestation)
      await db.nurtureEvents.update(nurtureEvent.id, { synced: true })
      await db.eventLog.add({
        id: createId(),
        kind: 'attestation-created',
        payload: {
          jobId: job.id,
          kind: 'nurtureEvent',
          attestationUid: attestation.id,
        },
        timestamp: nowIso(),
      })
    })
  },
}

const evolutionEventHandler: JobHandler = {
  type: 'evolution-event',
  async process(job: Job) {
    const creatureId = String(job.payload.creatureId ?? '')
    const playerId = String(job.payload.playerId ?? '')
    const fromStage = String(job.payload.fromStage ?? '') as Stage
    const toStage = String(job.payload.toStage ?? '') as Stage
    if (!creatureId || !playerId || !fromStage || !toStage) {
      throw new Error('evolution-event job missing creatureId, playerId, fromStage, or toStage')
    }

    const player = await db.players.get(playerId)
    if (!player) {
      throw new Error('evolution-event references missing player')
    }

    const { createEASOffchainAttestation } = await loadAttestation()
    const attestation = await createEASOffchainAttestation({
      schemaKey: 'wefa.evolution',
      recipient: player.address,
      data: {
        creatureId,
        fromStage: stageToEnum(fromStage),
        toStage: stageToEnum(toStage),
        xpSpent: Number(job.payload.xpSpent ?? 0),
        timestamp:
          typeof job.payload.timestamp === 'string'
            ? toUnixSeconds(job.payload.timestamp)
            : Math.floor(Date.now() / 1000),
      },
    })

    await db.transaction('rw', db.attestations, db.eventLog, async () => {
      await db.attestations.put(attestation)
      await db.eventLog.add({
        id: createId(),
        kind: 'attestation-created',
        payload: {
          jobId: job.id,
          kind: 'evolution',
          attestationUid: attestation.id,
        },
        timestamp: nowIso(),
      })
    })

    await publishProofRefIfLinked(playerId, attestation.schemaId, attestation.id, 'evolution')
  },
}

const gameResultHandler: JobHandler = {
  type: 'game-result',
  async process(job: Job) {
    const sessionId = String(job.payload.sessionId ?? '')
    const playerId = String(job.payload.playerId ?? '')
    if (!sessionId || !playerId) {
      throw new Error('game-result job missing sessionId or playerId')
    }

    const [session, player] = await Promise.all([
      db.gameSessions.get(sessionId),
      db.players.get(playerId),
    ])
    if (!session || !player) {
      throw new Error('game-result references missing game session or player')
    }

    const winner =
      typeof job.payload.winner === 'number'
        ? job.payload.winner
        : job.payload.winner === 'draw'
          ? 255
          : 0

    const { createEASOffchainAttestation, toBytes32Hash } = await loadAttestation()
    const attestation = await createEASOffchainAttestation({
      schemaKey: 'wefa.gameResult',
      recipient: player.address,
      data: {
        sessionId: session.id,
        gameType: gameTypeToEnum(session.type),
        mode: modeToEnum(job.payload.mode as 'device' | 'local' | 'world' | undefined),
        players: JSON.stringify([session.hostId, session.guestId ?? '']),
        winner,
        joinCodeHash: toBytes32Hash(session.joinCode ?? ''),
        timestamp:
          typeof job.payload.timestamp === 'string'
            ? toUnixSeconds(job.payload.timestamp)
            : Math.floor(Date.now() / 1000),
      },
    })

    await db.transaction('rw', db.attestations, db.eventLog, async () => {
      await db.attestations.put(attestation)
      await db.eventLog.add({
        id: createId(),
        kind: 'attestation-created',
        payload: {
          jobId: job.id,
          kind: 'gameResult',
          attestationUid: attestation.id,
        },
        timestamp: nowIso(),
      })
    })

    await publishProofRefIfLinked(playerId, attestation.schemaId, attestation.id, 'gameResult')
  },
}

const identityLinkHandler: JobHandler = {
  type: 'identity-link',
  async process(job: Job) {
    const playerId = String(job.payload.playerId ?? '')
    const atDid = String(job.payload.atDid ?? '')
    const evmAddress = String(job.payload.evmAddress ?? '')
    if (!playerId || !atDid || !evmAddress) {
      throw new Error('identity-link job missing playerId, atDid, or evmAddress')
    }

    const { ensureIdentityLinkAttestation } = await loadAtproto()
    await ensureIdentityLinkAttestation({
      playerId,
      atDid,
      evmAddress,
      chainId: 84532,
    })
  },
}

const handlers: JobHandler[] = [
  careEventHandler,
  nurtureEventHandler,
  evolutionEventHandler,
  gameResultHandler,
  identityLinkHandler,
]

let hasRegisteredDefaultHandlers = false

export function registerDefaultJobHandlers(): void {
  if (hasRegisteredDefaultHandlers) return
  for (const handler of handlers) {
    registerHandler(handler)
  }
  hasRegisteredDefaultHandlers = true
}

export function resetDefaultJobHandlersForTests(): void {
  hasRegisteredDefaultHandlers = false
}
